// add-foreign-key / drop-foreign-key edit planner (v2 Slice B2).
//
// PRD: Ralph/DdlStructuralEdit2/PRD.md § Slice B2.
//
// Both ops append a fresh ALTER TABLE statement after the source statement
// (DSE2-7: no coalescing by default; opt-in coalescing is slice H1). Source
// path may be either ALTER TABLE or CREATE TABLE — the synthesized statement
// targets the same table named by the source structure.

import { rangeFromOffsets } from '../../../sql-grammar/document/lineIndex';
import type { QualifiedName, SqlEngine } from '../../../sql-grammar/model';
import { readQualifiedName } from '../../scope/identifiers';
import type { DdlEdit, DdlStructure, ForeignKeyDef, PlannedByteEdit } from '../../types';

import { pickAppendInsertion } from './appendInsertion';
import type { DdlEditCtx } from './applyDdlEdit';
import { sourceSpellingOrigin } from './columnEdit.alterTable';
import {
  synthesizeAddForeignKey,
  synthesizeDropForeignKey,
} from './dialect.foreignKey';
import { decodeTypedColumnList, namesSameRelation } from './existingNameEvidence';

type ForeignKeyEdit = Extract<DdlEdit, { op: 'add-foreign-key' | 'drop-foreign-key' }>;

export function planForeignKeyEdit(
  ctx: DdlEditCtx,
  edit: ForeignKeyEdit,
): readonly PlannedByteEdit[] {
  const table = pickTable(ctx.structure);
  if (!table) {
    throw new Error(
      `applyDdlEdit: cannot apply ${edit.op} edit to ${ctx.structure.kind} statement`,
    );
  }
  const synth = edit.op === 'add-foreign-key'
    ? synthesizeAdd(ctx, table, edit.foreignKey)
    : synthesizeDropForeignKey(ctx.engine, table, edit.constraintName, edit.cascade);

  const { offset: insertAt, sep, trailSep } = pickAppendInsertion(
    ctx.source,
    ctx.statementInfo.range.endOffset,
  );
  return [{
    range: rangeFromOffsets(ctx.lineIdx, insertAt, insertAt),
    replacement: sep + synth + trailSep,
    rationale: `${edit.op}:alter-table`,
  }];
}

function pickTable(s: DdlStructure): QualifiedName | undefined {
  if (s.kind === 'alter-table') return s.table;
  if (s.kind === 'create-table') return s.table;
  return undefined;
}

/** Decode the caller's column lists, then hand the synthesizer names plus the
 *  origin each one answers with — the same two-step `primaryKeyEdit.ts` runs,
 *  because a FOREIGN KEY names existing columns under the identical contract.
 *
 *  The two lists answer SEPARATELY, and that is the whole point of the split:
 *  the owner side names columns of `pickTable(ctx.structure)`, which is the
 *  source statement's own table, so its clauses (or a CREATE TABLE's column
 *  list) answer for them directly. The ref side names columns of
 *  `fk.refTable`, which is a different relation UNLESS this statement's own
 *  table is what that text spells — see {@link refNamesSourceRelation}.
 *
 *  Each list answers BY POSITION, because within one list the position is the
 *  only thing that identifies an element: `Id` and `"Id"` decode to the same
 *  bytes and name two different columns on a folding engine, so a by-name
 *  lookup serves the second element's typed intent back for the first and the
 *  key names one column twice. */
function synthesizeAdd(
  ctx: DdlEditCtx,
  table: QualifiedName,
  fk: ForeignKeyDef,
): string {
  const keys = decodeTypedColumnList(ctx.engine, fk.columns);
  const refKeys = decodeTypedColumnList(ctx.engine, fk.refColumns);
  const decoded: ForeignKeyDef = {
    ...fk,
    columns: keys.columns,
    refColumns: refKeys.columns,
  };
  const selfReferencing = refNamesSourceRelation(ctx.engine, table, fk.refTable);
  return synthesizeAddForeignKey(ctx.engine, table, decoded, {
    quoteName: table.quoted,
    columnNameOrigin: (index) => keys.typedOriginAt(index)
      ?? sourceSpellingOrigin(ctx.structure, keys.columns[index]),
    refColumnNameOrigin: (index) => refKeys.typedOriginAt(index)
      ?? (selfReferencing
        ? sourceSpellingOrigin(ctx.structure, refKeys.columns[index])
        : 'bare-reference'),
  });
}

/** Whether `refTable` — verbatim caller text, never requoted (`ForeignKeyDef`)
 *  — names the very relation this statement targets. Only a `true` here lets
 *  the source's column spellings answer for the REFERENCED side, so what
 *  counts as proof is deliberately narrow:
 *
 *    • the text must READ AS ONE identifier chain, whole. `readQualifiedName`
 *      is the same reader the resolver uses, so `"users"`, `[dbo].[users]` and
 *      `` `users` `` are all understood on the engines that write them — but
 *      anything it does not consume to the last byte (`users u`, `users, x`, a
 *      function call) is not a relation name this code can reason about, and
 *      proves nothing;
 *    • the chain's identity must then equal the source table's COMPONENT BY
 *      COMPONENT (`namesSameRelation`), which also means a qualifier one side
 *      carries and the other does not leaves the question open — a bare
 *      `users` is `public.users` only if the session's search path says so,
 *      and `applyDdlEdit` has no session to ask.
 *
 *  Everything unproven keeps `'bare-reference'`, which is what the ref side
 *  was under unconditionally before: unrelated tables stay independent. */
function refNamesSourceRelation(
  engine: SqlEngine,
  table: QualifiedName,
  refTable: string,
): boolean {
  const typed = refTable.trim();
  if (typed.length === 0) return false;
  const read = readQualifiedName(typed, 0, typed.length, { engine });
  if (!read || read.range.endOffset !== typed.length) return false;
  return namesSameRelation(table, read.qualified, engine);
}
